import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import Logo from './Logo';

const PANEL_URL = 'https://panel.cafeotomasyonu.com';

/**
 * Vitrinin üst menüsü.
 *
 * Ana sayfada ve modül sayfalarında HİÇ üst menü yoktu: ziyaretçi bir modül
 * sayfasından fiyatlara, rehbere ya da kasa uygulamasının indirme sayfasına
 * ancak sayfanın en altındaki footer'a inerek ulaşabiliyordu. Yeni bir
 * yetenek (3D/AR menü) eklendiğinde ona yönlendirecek ilk yer de burası.
 *
 * Fiyatlar ana sayfanın bir bölümü: başka sayfadan `/#fiyatlandirma` ile
 * gelindiğinde kaydırmayı ana sayfa kendisi yapıyor (hash okuması orada).
 */
const NAV_LINKS = [
  { to: '/moduller', label: 'Modüller' },
  { to: '/ar-menu', label: '3D/AR Menü', badge: 'Yeni' },
  { to: '/solutions', label: 'Çözümler' },
  { to: '/#fiyatlandirma', label: 'Fiyatlar' },
  { to: '/guide', label: 'Rehber' },
  { to: '/download', label: 'Kasa Uygulaması', icon: 'Download' }
];

const SiteHeader = ({ tone = 'light' }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Sayfa değişince mobil menü açık kalmasın.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const dark = tone === 'dark';
  const shell = dark
    ? 'bg-gray-950/85 border-white/10 text-white'
    : 'bg-white/90 border-gray-200 text-foreground';
  const linkIdle = dark ? 'text-gray-300 hover:text-white' : 'text-text-secondary hover:text-primary';
  const linkActive = dark ? 'text-white' : 'text-primary';

  const isActive = (to) => !to.includes('#') && location.pathname === to;

  return (
    <header className={`sticky top-0 z-40 backdrop-blur border-b ${shell}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <Logo size={32} />
            <span className="font-poppins font-heading-bold text-lg">Cafe Otomasyonu</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Ana menü">
            {NAV_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`inline-flex items-center gap-1.5 font-inter font-accent-medium text-sm transition-colors ${
                  isActive(item.to) ? linkActive : linkIdle
                }`}
              >
                {item.icon && <Icon name={item.icon} size={15} />}
                {item.label}
                {item.badge && (
                  <span className="px-1.5 py-0.5 rounded-md bg-primary text-white text-[10px] leading-none font-semibold">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`${PANEL_URL}/login`}
              className={`font-inter font-accent-medium text-sm ${linkIdle}`}
            >
              Giriş Yap
            </a>
            <a
              href={`${PANEL_URL}/sign-up`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-primary text-white font-poppins font-heading-semibold text-sm shadow hover:opacity-95 transition"
            >
              Ücretsiz Başla
            </a>
          </div>

          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className={`lg:hidden p-2 rounded-lg ${dark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            aria-expanded={open}
            aria-label={open ? 'Menüyü kapat' : 'Menüyü aç'}
          >
            <Icon name={open ? 'X' : 'Menu'} size={22} />
          </button>
        </div>
      </div>

      {open && (
        <div className={`lg:hidden border-t ${dark ? 'border-white/10 bg-gray-950' : 'border-gray-200 bg-white'}`}>
          <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col" aria-label="Mobil menü">
            {NAV_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between py-3 font-inter font-accent-medium ${
                  isActive(item.to) ? linkActive : linkIdle
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  {item.icon && <Icon name={item.icon} size={16} />}
                  {item.label}
                </span>
                {item.badge && (
                  <span className="px-2 py-0.5 rounded-md bg-primary text-white text-xs font-semibold">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
            <div className="flex gap-3 pt-3 pb-2">
              <a
                href={`${PANEL_URL}/login`}
                className={`flex-1 text-center px-4 py-3 rounded-lg border font-inter font-accent-medium ${
                  dark ? 'border-white/20' : 'border-gray-300'
                }`}
              >
                Giriş Yap
              </a>
              <a
                href={`${PANEL_URL}/sign-up`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center px-4 py-3 rounded-lg bg-primary text-white font-poppins font-heading-semibold"
              >
                Ücretsiz Başla
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
